import type { OrderView } from '@khaddi/contracts';
import { ErrorNote, Icon, IconButton, Screen, Spinner, Text, colors, formatDateTime, formatVnd, useApi, useAsync, useLayout } from '@khaddi/ui';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { CollectBox, CustomerCard, ItemsList } from '../components/DeliveryParts';
import { useShipperNav } from '../components/ShipperNav';
import { TabHeader } from '../components/TabHeader';
import { paymentName } from '../format';

/** `onSelect` makes the row a button that opens the order in the detail pane (wide screens). */
const HistoryRow = ({ order, selected = false, onSelect }: { order: OrderView; selected?: boolean; onSelect?: () => void }) => {
  const cod = order.paymentMethod === 'cod';
  const card = { backgroundColor: colors.surface, borderRadius: 16, paddingVertical: 12, paddingHorizontal: 14, gap: 6 } as const;
  const content = (
    <>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <Text variant="bodyStrong">
          #{order.code} · {order.cupCount} ly
        </Text>
        <Text variant="bodyStrong" color={colors.green} numeric>
          +{formatVnd(order.shipperFee)}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', gap: 6, alignItems: 'flex-start' }}>
        <Icon name="pin" size={14} color={colors.textMuted} />
        <Text variant="caption" style={{ flex: 1 }} numberOfLines={2}>
          {order.delivery?.address ?? order.branch.name}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 8 }}>
        <Text variant="caption" numeric>
          {formatDateTime(order.updatedAt)}
        </Text>
        <Text variant="caption" color={cod ? colors.goldText : colors.textMuted} style={{ fontWeight: cod ? '700' : '500' }} numeric>
          {cod ? `COD ${formatVnd(order.pricing.total)}` : `Đã trả ${paymentName(order)}`}
        </Text>
      </View>
    </>
  );
  if (!onSelect) return <View style={card}>{content}</View>;
  return (
    <Pressable accessibilityRole="button" accessibilityState={{ selected }} onPress={onSelect} style={[card, { borderWidth: 2, borderColor: selected ? colors.primaryText : 'transparent' }]}>
      {content}
    </Pressable>
  );
};

const HistoryDetail = ({ order }: { order: OrderView }) => (
  <View style={{ backgroundColor: colors.surface, borderRadius: 18, padding: 16, gap: 14 }}>
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <View style={{ flex: 1 }}>
        <Text variant="caption" numeric>
          {formatDateTime(order.updatedAt)} · {order.branch.name}
        </Text>
        <View accessibilityRole="header">
          <Text variant="title">
            Đơn #{order.code} · {order.cupCount} ly
          </Text>
        </View>
      </View>
      <Text variant="title" color={colors.green} numeric>
        +{formatVnd(order.shipperFee)}
      </Text>
    </View>
    <CustomerCard order={order} />
    <ItemsList order={order} />
    <View style={{ flexDirection: 'row' }}>
      <CollectBox order={order} />
    </View>
  </View>
);

export const HistoryScreen = () => {
  const nav = useShipperNav('history');
  const api = useApi();
  const history = useAsync(() => api.shipper.history(), []);
  const orders = history.data ?? [];
  const { wide } = useLayout();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = orders.find((o) => o.id === selectedId) ?? orders[0] ?? null;

  return (
    <Screen nav={nav}>
      <TabHeader
        title="Lịch sử giao"
        subtitle={history.data ? `${orders.length} đơn gần nhất` : undefined}
        right={<IconButton icon="history" label="Làm mới lịch sử" onPress={() => void history.reload()} />}
      />
      {history.loading && !history.data ? <Spinner /> : null}
      {history.error ? <ErrorNote message={history.error} onRetry={() => void history.reload()} /> : null}
      <View style={{ paddingHorizontal: 16, gap: 10 }}>
        {history.data && orders.length === 0 ? (
          <View style={{ backgroundColor: colors.surface, borderRadius: 16, padding: 20, alignItems: 'center', gap: 6 }}>
            <Icon name="receipt" size={28} color={colors.textSubtle} />
            <Text variant="bodyStrong">Chưa có đơn nào</Text>
            <Text variant="caption">Đơn đã giao xong sẽ hiện ở đây.</Text>
          </View>
        ) : null}
        {wide ? null : orders.map((order) => <HistoryRow key={order.id} order={order} />)}
      </View>
      {wide && selected ? (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 16, paddingHorizontal: 16 }}>
          <View style={{ flex: 2, minWidth: 0, gap: 10 }}>
            {orders.map((order) => (
              <HistoryRow key={order.id} order={order} selected={order.id === selected.id} onSelect={() => setSelectedId(order.id)} />
            ))}
          </View>
          <View style={{ flex: 3, minWidth: 0, position: 'sticky' as unknown as 'relative', top: 16 }}>
            <HistoryDetail order={selected} />
          </View>
        </View>
      ) : null}
    </Screen>
  );
};
